// Integration layer for font parity in rendering pipeline

import { FontLoadingService } from './FontLoadingService';
import type { FontLoadingConfig } from './FontLoadingService';

export type { FontLoadingConfig };
import { FontParityManager, FontDescriptor } from './FontParityManager';
import { BackendLayoutPlan } from './BackendPlanValidator';

export class FontParityIntegration {
  private fontLoadingService: FontLoadingService;

  constructor(config: FontLoadingConfig) {
    this.fontLoadingService = new FontLoadingService(config);
  }

  /**
   * Prepare fonts for rendering with parity verification
   */
  async prepareFontsForRendering(
    plan: BackendLayoutPlan
  ): Promise<{
    success: boolean;
    fontFamily: string;
    isExactMatch: boolean;
    errors: string[];
  }> {
    try {
      // Extract font information from backend plan
      const backendFont: FontDescriptor = {
        family: plan.fontFamily,
        weight: 400, // Could be extracted from plan if available
        style: 'normal',
        features: plan.features?.split(' ') || ['kern', 'liga'],
        source: 'backend',
        hash: plan.planHash // Use plan hash as font identifier
      };

      // Ensure font is ready
      const fontResult = await this.fontLoadingService.ensureFontReady(
        backendFont,
        { family: plan.fontFamily, size: plan.fontSizePx }
      );

      if (!fontResult.isExactMatch) {
        console.warn(`⚠️ [FontParityIntegration] Font parity not achieved for "${plan.fontFamily}"`);
      }

      return {
        success: true,
        fontFamily: fontResult.fontFamily,
        isExactMatch: fontResult.isExactMatch,
        errors: []
      };

    } catch (error) {
      return {
        success: false,
        fontFamily: plan.fontFamily, // Fallback to original
        isExactMatch: false,
        errors: [error instanceof Error ? error.message : 'Unknown font loading error']
      };
    }
  }

  /**
   * Configure canvas with verified font
   */
  configureCanvasWithVerifiedFont(
    ctx: CanvasRenderingContext2D,
    plan: BackendLayoutPlan,
    verifiedFontFamily: string
  ): void {
    const fontDescriptor: FontDescriptor = {
      family: verifiedFontFamily,
      weight: 400,
      style: 'normal',
      features: plan.features?.split(' ') || ['kern', 'liga'],
      source: 'backend'
    };

    FontParityManager.configureCanvasFont(ctx, fontDescriptor, plan.fontSizePx);
  }

  /**
   * Validate rendering consistency after font setup
   */
  validateRenderingConsistency(
    ctx: CanvasRenderingContext2D,
    sampleText: string,
    expectedWidth?: number
  ): boolean {
    const metrics = ctx.measureText(sampleText);
    
    if (expectedWidth && Math.abs(metrics.width - expectedWidth) > 1.0) {
      console.warn(`⚠️ [FontParityIntegration] Text width mismatch: expected ${expectedWidth}, got ${metrics.width}`);
      return false;
    }

    return true;
  }
}
